import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Loader2, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSectionsEnabled } from "@/lib/sections";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const PERIODS = [1, 2, 3, 4, 5, 6, 7, 8];

export default function TimetablePage() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [schoolTypes, setSchoolTypes] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [classOptions, setClassOptions] = useState([]);
  const [className, setClassName] = useState("");
  const [section, setSection] = useState("A");
  const useSec = useSectionsEnabled();
  const [year, setYear] = useState("2025-26");
  const [slots, setSlots] = useState({});
  const [saving, setSaving] = useState(false);
  const isGlobal = ["master_admin", "super_admin"].includes(user?.role);

  async function loadMeta() {
    const [b, s] = await Promise.all([api.get("/branches"), api.get("/meta/school-types")]);
    setBranches(b.data);
    setSchoolTypes(s.data);
    const def = user?.branch_id || b.data[0]?.id || "";
    setBranchId(def);
  }
  useEffect(() => { loadMeta().catch(() => {}); }, []);

  useEffect(() => {
    if (!branchId || schoolTypes.length === 0) return;
    const b = branches.find((x) => x.id === branchId);
    const st = schoolTypes.find((s) => s.id === b?.school_type);
    const classes = st?.classes || [];
    setClassOptions(classes);
    if (classes.length > 0 && !classes.includes(className)) setClassName(classes[0]);
  }, [branchId, branches, schoolTypes]); // eslint-disable-line

  async function load() {
    if (!branchId || !className) return;
    const { data } = await api.get("/timetable", { params: { branch_id: branchId, class_name: className, section, academic_year: year } });
    const map = {};
    (data.slots || []).forEach((s) => { map[`${s.day}-${s.period}`] = s; });
    setSlots(map);
  }
  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line */ }, [branchId, className, section, year]);

  function setCell(day, period, patch) {
    const key = `${day}-${period}`;
    setSlots({ ...slots, [key]: { ...(slots[key] || { day, period }), ...patch } });
  }

  async function save() {
    const list = Object.values(slots).filter((s) => s.subject);
    setSaving(true);
    try {
      await api.put("/timetable", { branch_id: branchId, class_name: className, section, academic_year: year, slots: list });
      toast.success(`Timetable saved (${list.length} periods)`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  }

  const canEdit = ["master_admin", "super_admin", "principal", "teacher"].includes(user?.role);

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-wider font-semibold text-violet-700 mb-2">Weekly Schedule</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Timetable</h1>
        <p className="text-slate-500 mt-1.5">Build the weekly schedule for each class and section.</p>
      </div>

      <Card className="border-violet-100 mb-5">
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          {isGlobal && (
            <div>
              <Label>Branch</Label>
              <Select value={branchId} onValueChange={setBranchId}>
                <SelectTrigger data-testid="timetable-branch" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Class</Label>
            <Select value={className} onValueChange={setClassName}>
              <SelectTrigger data-testid="timetable-class" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{classOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {useSec && <div>
            <Label>Section</Label>
            <Input data-testid="timetable-section" value={section} onChange={(e) => setSection(e.target.value.toUpperCase())} className="mt-1.5 input-focus" />
          </div>}
          <div>
            <Label>Year</Label>
            <Input value={year} onChange={(e) => setYear(e.target.value)} className="mt-1.5 input-focus" />
          </div>
          {canEdit && (
            <div className="flex items-end">
              <Button data-testid="save-timetable" onClick={save} disabled={saving} className="w-full h-10 bg-violet-600 hover:bg-violet-700">
                {saving ? <Loader2 className="animate-spin" size={18} /> : <><Save size={16} className="mr-2" /> Save</>}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-violet-100 overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-sm">
              <thead className="bg-violet-50 text-slate-700">
                <tr>
                  <th className="px-3 py-3 text-left font-semibold">Period</th>
                  {DAYS.map((d) => (<th key={d} className="px-3 py-3 text-left font-semibold">{d}</th>))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {PERIODS.map((p) => (
                  <tr key={p}>
                    <td className="px-3 py-3 font-semibold text-slate-700 bg-slate-50">#{p}</td>
                    {DAYS.map((d) => {
                      const cell = slots[`${d}-${p}`] || {};
                      return (
                        <td key={d} className="px-2 py-2 align-top min-w-[140px]">
                          <input
                            data-testid={`tt-${d}-${p}-subject`}
                            value={cell.subject || ""}
                            onChange={(e) => setCell(d, p, { subject: e.target.value })}
                            disabled={!canEdit}
                            placeholder="Subject"
                            className="w-full border border-slate-200 rounded-md px-2 py-1 text-sm font-semibold text-slate-800 input-focus disabled:bg-transparent"
                          />
                          <input
                            data-testid={`tt-${d}-${p}-teacher`}
                            value={cell.teacher_name || ""}
                            onChange={(e) => setCell(d, p, { teacher_name: e.target.value })}
                            disabled={!canEdit}
                            placeholder="Teacher"
                            className="w-full mt-1 border border-transparent rounded-md px-2 py-0.5 text-xs text-slate-500 input-focus hover:border-slate-200 disabled:bg-transparent"
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </AppShell>
  );
}
